import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { CarCategoryService } from '@zaoblako/web/core/services/passage/car-category.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { findDefaultCarCategoryInList } from '@zaoblako/web/shared/utils/car-category-default-from-filtered-list.util';
import type { CarCategoryFindInterface } from '@zaoblako/shared/modules/cctv/dtos/car-category/car-category-find.dto';
import type { CarCategoryQueryInterface } from '@zaoblako/shared/modules/cctv/dtos/car-category/car-category-query.dto';

import { isEqual, isNil } from 'lodash';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'crm-car-category-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent],
  templateUrl: './car-category-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarCategorySingleSelectComponent implements OnInit, OnChanges {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public categories: CarCategoryFindInterface[] = [];
  public queryParams: CarCategoryQueryInterface = {
    limit: 40,
    offset: 0,
    select: [
      'id',
      'name',
      'transitTaskLimitsEnabled',
      'roomPrices'
    ]
  };

  public loading = false;
  public defaultCategoryLoading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedCategory: CarCategoryFindInterface;
  @Input() public set selectedCategory(selectedCategory: CarCategoryFindInterface) {
    if (this.customSearch) {
      this._selectedCategory = selectedCategory;
      return;
    }

    if (!selectedCategory?.id) {
      this._selectedCategory = undefined;
      return;
    }

    if (this.categories.some((category) => category.id === selectedCategory?.id)) {
      this._selectedCategory = this.categories.find((category) => category.id === selectedCategory.id);
      return;
    }

    if (selectedCategory?.id) {
      if (typeof selectedCategory.id === 'string') {
        selectedCategory.id = +selectedCategory.id;
      }

      this.carCategoryService.findById<CarCategoryFindInterface>(selectedCategory.id).subscribe({
        next: (_category) => {
          if (!this.categories.some((category) => category.id === _category.id)) {
            this.categories = this.categories.concat(_category);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedCategory = _category;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
    }
  }

  @Output() public selectedCategoryChange = new EventEmitter<CarCategoryFindInterface>();

  @Input() public customSearch = false;

  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public itemStyleClass = '';
  @Input() public showClear = false;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = false;
  @Input() public placeholder = '\u00A0';
  @Input() public appendTo: string;

  @Input() public onlyParkingAccessibleRooms = false;
  @Input() public onlyTransitTaskLimitsEnabled = false;
  /** When bound, filters categories by room IDs; empty value clears the list */
  @Input() public roomIds: number[] | null | undefined = undefined;
  @Input() public autoApplyDefaultWhenRoomsSelected = true;

  private lastAppliedRoomFilter?: { onlyParkingAccessibleRooms: boolean; roomIds: number[] | null | undefined };
  private appliedDefaultForRoomFilterKey?: string;
  private pendingDefaultApplyRoomFilterKey?: string;

  @Input() public set setSelectedCategoryId(id: number) {
    if (isNil(id)) {
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.categories.some((category) => category.id === _id)) {
      this._selectedCategory = this.categories.find((category) => category.id === _id);
      return;
    }

    this.carCategoryService.findById<CarCategoryFindInterface>(_id).subscribe({
      next: (_category) => {
        if (!this.categories.some((category) => category.id === _category.id)) {
          this.categories = this.categories.concat(_category);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedCategory = _category;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly carCategoryService: CarCategoryService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService
  ) {}

  public ngOnInit(): void {
    if (!this.shouldDeferInitialLoadToRoomFilter()) {
      this.loadCategories();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['roomIds'] && !changes['onlyParkingAccessibleRooms']) {
      return;
    }

    const nextFilter = {
      onlyParkingAccessibleRooms: this.onlyParkingAccessibleRooms,
      roomIds: this.roomIds
    };

    if (this.lastAppliedRoomFilter && isEqual(this.lastAppliedRoomFilter, nextFilter)) {
      return;
    }

    this.lastAppliedRoomFilter = nextFilter;
    this.appliedDefaultForRoomFilterKey = undefined;
    this.defaultCategoryLoading = false;
    this.loadCategories();
  }

  private shouldDeferInitialLoadToRoomFilter(): boolean {
    return this.isRoomIdsFilterBound() || this.onlyParkingAccessibleRooms;
  }

  public onSelect(category: CarCategoryFindInterface) {
    this.selectedCategoryChange.emit(category);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.categories.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.carCategoryService.findWithPagination(this.buildQueryParams()).subscribe({
                next: (data) => {
                  this.categories = this.categories.concat(
                    data.items.filter((item) => !this.categories.some((category) => category.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 50);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.carCategoryService.findWithPagination(this.buildQueryParams()).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedCategory?.id) && this._selectedCategory) {
            this.categories = data.items.concat(this._selectedCategory);
          } else {
            this.categories = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  private isRoomIdsFilterBound(): boolean {
    return this.roomIds !== undefined;
  }

  private buildQueryParams(): CarCategoryQueryInterface {
    const params: CarCategoryQueryInterface = {
      ...this.queryParams,
      onlyParkingAccessibleRooms: this.onlyParkingAccessibleRooms || undefined,
      onlyTransitTaskLimitsEnabled: this.onlyTransitTaskLimitsEnabled || undefined
    };

    if (this.isRoomIdsFilterBound()) {
      params.roomIds = this.roomIds?.length ? this.roomIds : undefined;
    }

    return params;
  }

  private loadCategories(): void {
    if (
      this.customSearch ||
      !this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.PHYSICAL_ACCESS_CONTROL?.enabled
    ) {
      return;
    }

    if (this.isRoomIdsFilterBound() && !this.roomIds?.length) {
      this.categories = [];
      this.count = 0;
      this.loading = false;
      this.paginationLoading = false;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();
      return;
    }

    this.defaultCategoryLoading = false;
    this.loading = true;
    this.queryParams.offset = 0;
    this.cdRef.markForCheck();

    this.carCategoryService.findWithPagination(this.buildQueryParams()).subscribe({
      next: (data) => {
        this.categories = data.items;
        this.count = data.count;
        this.loading = false;
        this.tryApplyDefaultCategoryAfterRoomsFiltered();
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  private getRoomFilterKeyForDefaultApply(): string | undefined {
    if (!this.isRoomIdsFilterBound() || !this.roomIds?.length) {
      return undefined;
    }

    return JSON.stringify({
      onlyParkingAccessibleRooms: this.onlyParkingAccessibleRooms,
      roomIds: this.roomIds
    });
  }

  private tryApplyDefaultCategoryAfterRoomsFiltered(): void {
    if (!this.autoApplyDefaultWhenRoomsSelected) {
      return;
    }

    const filterKey = this.getRoomFilterKeyForDefaultApply();
    if (!filterKey || filterKey === this.appliedDefaultForRoomFilterKey) {
      return;
    }

    if (this._selectedCategory?.id) {
      this.appliedDefaultForRoomFilterKey = filterKey;
      return;
    }

    if (!this.categories.length) {
      return;
    }

    if (this.pendingDefaultApplyRoomFilterKey === filterKey) {
      return;
    }

    this.pendingDefaultApplyRoomFilterKey = filterKey;
    this.defaultCategoryLoading = true;
    this.cdRef.markForCheck();

    findDefaultCarCategoryInList(() => this.carCategoryService.findDefault(), this.categories)
      .pipe(
        finalize(() => {
          this.defaultCategoryLoading = false;
          this.cdRef.markForCheck();
        })
      )
      .subscribe({
        next: (match) => {
          this.pendingDefaultApplyRoomFilterKey = undefined;

          if (filterKey !== this.getRoomFilterKeyForDefaultApply()) {
            return;
          }

          this.appliedDefaultForRoomFilterKey = filterKey;

          if (this._selectedCategory?.id || !match) {
            return;
          }

          this._selectedCategory = match;
          this.selectedCategoryChange.emit(match);
          this.cdRef.markForCheck();
        }
      });
  }
}
